import Swiper, { Autoplay, Navigation } from 'swiper';
import { swiperObserver } from '../../utils/swiper.utils.js';

const MAX_CARD_SIZE = 278;
const DESKTOP_FIT_BREAKPOINT = 1023;
const getInitialSlide = (slidesCount) => (window.innerWidth >= 641 && slidesCount > 3 ? 1 : 0);
const getSpaceBetween = () => (window.innerWidth >= 1201 ? 36 : 20);
const getAutoplayConfig = () => ({
  enabled: true,
  delay: 4000,
  disableOnInteraction: false,
  pauseOnMouseEnter: window.innerWidth >= 1033,
});
const getDisabledAutoplayConfig = () => ({
  enabled: false,
  delay: 4000,
  disableOnInteraction: false,
  pauseOnMouseEnter: false,
});
const shouldEnableSliderMotion = (slidesCount) => {
  if (window.innerWidth <= 640) {
    return true;
  }

  if (window.innerWidth <= 1022) {
    return slidesCount > 2;
  }

  return slidesCount > 4;
};

const initCharityPurposeSwiper = (scope = document) => {
  const wrappers = scope.querySelectorAll('.charity-purpose-swiper-wr');

  wrappers.forEach((el) => {
    const swiperEl = el.querySelector('.swiper');

    if (!swiperEl || swiperEl.swiper) {
      return;
    }

    const nextEl = el.querySelector('.swiper-button-next');
    const prevEl = el.querySelector('.swiper-button-prev');
    const slidesCount = el.querySelectorAll('.swiper-slide').length;

    if (!slidesCount) {
      return;
    }

    const initialSlide = getInitialSlide(slidesCount);
    let autoplayObserver = null;
    let lastCardSize = null;
    let lastSpaceBetween = null;
    let resizeFrame = null;

    const syncSliderMetrics = (swiper = null) => {
      const spaceBetween = getSpaceBetween();
      const shouldFitContainer = window.innerWidth >= DESKTOP_FIT_BREAKPOINT;
      let nextCardSize = null;

      if (shouldFitContainer) {
        const availableWidth = el.clientWidth - (spaceBetween * 3);
        const cardSize = Math.min(MAX_CARD_SIZE, availableWidth / 4);

        nextCardSize = `${Math.max(cardSize, 0).toFixed(2)}px`;
      }

      const hasCardSizeChanged = nextCardSize !== lastCardSize;
      const hasSpaceBetweenChanged = spaceBetween !== lastSpaceBetween;

      if (hasCardSizeChanged) {
        if (nextCardSize) {
          el.style.setProperty('--focus-card-size', nextCardSize);
        } else {
          el.style.removeProperty('--focus-card-size');
        }

        lastCardSize = nextCardSize;
      }

      if (hasSpaceBetweenChanged) {
        lastSpaceBetween = spaceBetween;
      }

      if (swiper && !swiper.destroyed && (hasCardSizeChanged || hasSpaceBetweenChanged)) {
        swiper.params.spaceBetween = spaceBetween;
        swiper.update();
      }
    };

    const syncSlideRows = (swiper) => {
      const isDesktop = window.innerWidth >= 641;
      const topOffset = isDesktop ? -56 : 0;
      const sideRotate = 4;

      swiper.slides.forEach((slide, index) => {
        const progress = Number.isFinite(slide.progress)
          ? slide.progress
          : swiper.activeIndex - index;
        const offset = -progress;
        let rowShift = Math.abs(offset);
        let rotate = 0;

        if (!isDesktop) {
          rowShift = 0;
        } else {
          if (offset < 0) {
            rowShift = Math.abs(offset);
          } else if (offset > 1) {
            rowShift = offset - 1;
          } else {
            rowShift = 0;
          }
        }

        rowShift = Math.min(Math.max(rowShift, 0), 1);

        if (rowShift) {
          rotate = (offset < 0 ? sideRotate : -sideRotate) * rowShift;
        }

        slide.style.setProperty('--card-translate-y', `${topOffset * rowShift}px`);
        slide.style.setProperty('--card-rotate', `${rotate}deg`);
        slide.style.setProperty('--content-rotate', `${-rotate}deg`);
      });
    };

    const syncManualScroll = (swiper) => {
      const allowTouchMove = shouldEnableSliderMotion(slidesCount);

      swiper.params.allowTouchMove = allowTouchMove;
      swiper.allowTouchMove = allowTouchMove;
    };

    const syncAutoplay = (swiper) => {
      const enableAutoplay = shouldEnableSliderMotion(slidesCount);

      if (!enableAutoplay) {
        autoplayObserver?.disconnect();
        autoplayObserver = null;

        if (typeof swiper.autoplay?.stop === 'function') {
          swiper.autoplay.stop();
        }

        swiper.params.autoplay = getDisabledAutoplayConfig();
        return;
      }

      swiper.params.autoplay = getAutoplayConfig();

      if (!autoplayObserver) {
        autoplayObserver = swiperObserver(swiper);
      }

      if (typeof swiper.autoplay?.start === 'function' && !swiper.autoplay.running) {
        swiper.autoplay.start();
      }
    };

    const swiperConfig = {
      modules: [Autoplay, Navigation],
      observer: true,
      observeParents: true,
      slidesPerView: 'auto',
      spaceBetween: getSpaceBetween(),
      centeredSlides: false,
      initialSlide,
      speed: 400,
      threshold: 10,
      preventInteractionOnTransition: true,
      watchSlidesProgress: true,
      allowTouchMove: shouldEnableSliderMotion(slidesCount),
      autoplay: shouldEnableSliderMotion(slidesCount) ? getAutoplayConfig() : getDisabledAutoplayConfig(),
      breakpoints: {
        320: {
          slidesPerView: 'auto',
          spaceBetween: 20,
          centeredSlides: false,
        },
        641: {
          slidesPerView: 'auto',
          spaceBetween: 20,
          centeredSlides: false,
        },
        1201: {
          slidesPerView: 'auto',
          spaceBetween: 36,
          centeredSlides: false,
        },
      },
      on: {
        init(swiper) {
          if (initialSlide && swiper.activeIndex !== initialSlide) {
            swiper.slideTo(initialSlide, 0, false);
          }

          syncSlideRows(swiper);
          syncManualScroll(swiper);
          syncAutoplay(swiper);
        },
        breakpoint(swiper) {
          syncSliderMetrics(swiper);

          const targetSlide = getInitialSlide(slidesCount);

          if (targetSlide && swiper.activeIndex === 0) {
            swiper.slideTo(targetSlide, 0, false);
          }

          syncSlideRows(swiper);
          syncManualScroll(swiper);
          syncAutoplay(swiper);
        },
        setTranslate(swiper) {
          syncSlideRows(swiper);
        },
        slideChange(swiper) {
          syncSlideRows(swiper);
        },
        resize(swiper) {
          syncSliderMetrics(swiper);
          syncSlideRows(swiper);
          syncManualScroll(swiper);
          syncAutoplay(swiper);
        },
        setTransition(swiper, duration) {
          swiper.slides.forEach((slide) => {
            const card = slide.querySelector('.charity-purpose-sec__focus-card');
            const title = slide.querySelector('.charity-purpose-sec__focus-card-title');

            if (card) {
              card.style.transition = `transform ${duration}ms ease`;
            }

            if (title) {
              title.style.transition = `transform ${duration}ms ease`;
            }
          });
        },
        transitionEnd(swiper) {
          swiper.slides.forEach((slide) => {
            const card = slide.querySelector('.charity-purpose-sec__focus-card');
            const title = slide.querySelector('.charity-purpose-sec__focus-card-title');

            if (card) {
              card.style.transition = '';
            }

            if (title) {
              title.style.transition = '';
            }
          });
        },
      },
    };

    if (nextEl && prevEl) {
      swiperConfig.navigation = { nextEl, prevEl };
    }

    syncSliderMetrics();

    const swiper = new Swiper(swiperEl, swiperConfig);

    if (initialSlide && swiper.activeIndex !== initialSlide) {
      swiper.slideTo(initialSlide, 0, false);
    }

    syncSlideRows(swiper);
    syncManualScroll(swiper);
    syncAutoplay(swiper);

    window.addEventListener('resize', () => {
      if (resizeFrame) {
        return;
      }

      resizeFrame = requestAnimationFrame(() => {
        resizeFrame = null;
        syncSliderMetrics(swiper);
        syncSlideRows(swiper);
        syncManualScroll(swiper);
        syncAutoplay(swiper);
      });
    });
  });
};

initCharityPurposeSwiper();
window.initCharityPurposeSwiper = initCharityPurposeSwiper;
